import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import RawHtml from 'common/RawHtml';
import Spinner from 'common/Spinner';
import Field from 'containers/Field';
import { withStyles } from 'theme/withStyles';
import { withFieldProps } from 'hooks/useFieldProps';
import radioStyles from 'styles/radio';
import ErrorMsg from 'common/ErrorMsg';
import Help from 'common/Help';
import Tooltip from 'common/Tooltip';
import visuallyHidden from 'styles/visuallyHidden';
import { useSelector } from 'react-redux';
import rootStyles from './styles/root';

const styles = (theme) => {
  const { palette, spacing, typography } = theme;
  return {
    horiz: {},
    input: {
      ...radioStyles(theme),
      '&$horiz': {
        left: 0,
        position: 'absolute',
      },
    },
    option: {
      '& label': {
        ...typography.p,
        ...typography.p3,
      },
      marginTop: spacing.xs,
      paddingLeft: 34,
      position: 'relative',
    },
    root: {
      ...rootStyles(theme),
      '& legend': {
        ...typography.p,
        ...typography.p2,
        color: ({ error }) => (error ? palette.error : typography.p2.color),
      },
      '&.form-inline': {
        '& $option': {
          display: 'inline-block',
          marginRight: spacing.xs,
        },
      },
      padding: '0px !important',
    },
    visuallyHidden,
  };
};

const LookupHorizontal = ({
  classes,
  error,
  fieldDomId,
  fieldOptionalLabelSuffix,
  help,
  horizontal,
  label,
  loading,
  lookupFieldMap,
  name,
  onChange,
  options,
  placeholder,
  readOnly,
  required,
  tooltip,
  validate,
  value,
}) => {
  const optionalLabel = !required
    ? `<small> ${fieldOptionalLabelSuffix || '(Optional)'}</small>`
    : '';
  const labelString = `${label}${optionalLabel}`;

  const plaidEnabled = useSelector(
    ({ groups }) => groups?.FUNDING_TRANSFER_EXT?.configs?.isPlaidEnabled,
  );

  const hideAccountTypeLabel =
    fieldDomId.includes('appRequest_fundingAccountType') && !plaidEnabled;

  return (
    <>
      <fieldset
        className={classnames(
          'form-group',
          { error, 'form-inline': horizontal, loading },
          classes.root,
        )}
      >
        <legend className={hideAccountTypeLabel && classes.visuallyHidden}>
          <RawHtml type="span">{labelString}</RawHtml>
          {required && (
            <span className={classnames('hidden', classes.visuallyHidden)}>Required</span>
          )}
        </legend>
        <Spinner loading={loading} />
        <Tooltip tooltip={tooltip} labeledBy={`label-${fieldDomId}`} handleYOffset={-20} />
        <Help help={help} id={`help-${name}`} />
        {Object.keys(options).map((key) => (
          <Fragment key={key}>
            <div className={classnames('radio form-group', { error, loading }, classes.option)}>
              <input
                type="radio"
                aria-describedby={
                  classnames({
                    [`help-${name}`]: help,
                    [`error-${name}`]: error,
                  }) || undefined
                }
                name={fieldDomId}
                checked={value === key}
                id={`${fieldDomId}-${key}`}
                placeholder={placeholder}
                readOnly={readOnly}
                className={classnames(classes.input, classes.horiz)}
                onChange={() => {
                  onChange(name, key);
                  validate();
                }}
                value={key}
              />{' '}
              <RawHtml type="label" htmlFor={`${fieldDomId}-${key}`}>
                {options[key]}
              </RawHtml>
            </div>
            {lookupFieldMap ? (
              <div
                className={classnames(`region-composed-fields ${key}`, {
                  hidden: key !== value,
                })}
              >
                {(lookupFieldMap[key] || []).map((fieldName) => (
                  <Field key={fieldName} fieldName={fieldName} groupName="LOOKUP_COMPOSITE" />
                ))}
              </div>
            ) : null}
          </Fragment>
        ))}
      </fieldset>

      <ErrorMsg name={name} />
    </>
  );
};

LookupHorizontal.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  error: PropTypes.bool.isRequired,
  fieldDomId: PropTypes.string.isRequired,
  fieldOptionalLabelSuffix: PropTypes.string.isRequired,
  help: PropTypes.string.isRequired,
  horizontal: PropTypes.bool.isRequired,
  label: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  lookupFieldMap: PropTypes.objectOf(PropTypes.array),
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.shape({}).isRequired,
  placeholder: PropTypes.string.isRequired,
  readOnly: PropTypes.bool.isRequired,
  required: PropTypes.bool.isRequired,
  tooltip: PropTypes.string.isRequired,
  validate: PropTypes.func.isRequired,
  value: PropTypes.string.isRequired,
};

LookupHorizontal.defaultProps = {
  lookupFieldMap: null,
};

const LookupHorizontalStyled = withStyles(styles)(LookupHorizontal);

const mapStateToProps = ({ fields, resources }, { name }) => {
  const field = fields[name];
  return {
    fieldOptionalLabelSuffix: resources.fieldOptionalLabelSuffix ?? '',
    horizontal: field.layout === 'radioHorizontal',
    lookupFieldMap: field.lookupFieldMap,
    options: field.lookupValues,
  };
};

export default withFieldProps(mapStateToProps)(LookupHorizontalStyled);
